import { useState, useEffect } from "react";
import { Phone, ShoppingCart, User, LogOut, ShoppingBag, Settings, LayoutDashboard, Download } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSessionTimeout, setStayLoggedIn } from "@/hooks/useSessionTimeout";
import bawarchiLogo from "@/assets/bawarchi-logo.png";

interface HeaderProps {
  onOrderClick: () => void;
}

const Header = ({ onOrderClick }: HeaderProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [isStaff, setIsStaff] = useState(false);

  // Enable session timeout only when user is logged in
  useSessionTimeout(!!user);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkStaffRole(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkStaffRole(session.user.id);
      } else {
        setIsStaff(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkStaffRole = async (userId: string) => {
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .in("role", ["admin", "kitchen", "sales", "marketing", "delivery"]);
    
    setIsStaff(roles && roles.length > 0);
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error("Sign out error:", error);
    }
    // Force clear any remaining auth data from localStorage
    localStorage.removeItem('sb-rtbbonqykwfiuesigcjl-auth-token');
    // Clear "stay logged in" preference
    setStayLoggedIn(false);
    // Clear state
    setUser(null);
    setIsStaff(false);
    toast({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
    // Force page reload to ensure clean state
    window.location.href = "/";
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pt-[env(safe-area-inset-top)]">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={bawarchiLogo} alt="Bawarchi 149 Express" className="h-12 w-12" />
          <div>
            <h1 className="text-xl font-bold text-foreground">Bawarchi 149</h1>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Express</p>
          </div>
        </div>
        
        <nav className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/install-guide")}
            className="hidden sm:flex items-center gap-2 text-primary"
          >
            <Download className="h-4 w-4" />
            Install App
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/install-guide")}
            className="sm:hidden text-primary"
            aria-label="Install app"
          >
            <Download className="h-4 w-4" />
          </Button>
          
          <a href="tel:+639953947586" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" aria-label="Call us at +63 995 394 7586">
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">+63 995 394 7586</span>
          </a>
          
          {user ? (
            <>
              {isStaff && (
                <>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate("/admin")}
                    className="hidden sm:flex items-center gap-2"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Admin
                  </Button>
                  <Button
                    variant="default"
                    size="icon"
                    onClick={() => navigate("/admin")}
                    className="sm:hidden"
                    aria-label="Admin dashboard"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/my-orders")}
                className="hidden sm:flex items-center gap-2"
              >
                <ShoppingBag className="h-4 w-4" />
                My Orders
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/profile")}
                className="hidden sm:flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Profile
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="hidden sm:flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
              {/* Mobile - Only icons */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/my-orders")}
                className="sm:hidden"
                aria-label="My orders"
              >
                <ShoppingBag className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/profile")}
                className="sm:hidden"
                aria-label="Profile settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSignOut}
                className="sm:hidden"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/customer-auth")}
              className="gap-2"
            >
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Sign In</span>
            </Button>
          )}
          
          <Button variant="default" size="sm" className="gap-2" onClick={onOrderClick}>
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Order Now</span>
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
